import { Divider, Grid, IconButton, Typography } from "@mui/material";
import { useEffect, useState, useCallback, useMemo } from "react";
import { ActionButton } from "../../../../components/Button";
import Dialog, { DialogSectionTitle } from "../../../../components/Dialog";
import { IAddEditDialogProps } from "../../../../components/Dialog";
import TextField from "../../../../components/TextField";
import Switch from "../../../../components/Switch";
import { useLanguageContext } from "../../../../contexts/LanguageContext";
import IUser, { IUserAccessProfileEnterprise } from "../../../../interfaces/IUser";
import ValidationHelper from '../../../../helpers/ValidationHelper';
import DefaultConstants from "../../../../data/Constants";
import { RegistrationStatusData, RegistrationStatusEnum } from "../../../../data/Enums";
import FormHelper from "../../../../helpers/FormHelper";
import UserService from '../../../../services/UserService';
import IAccessProfile from "../../../../interfaces/IAccessProfile";
import AccessProfileService from "../../../../services/AccessProfileService";
import IEnterprise from "../../../../interfaces/IEnterprise";
import SelectField from "../../../../components/SelectField";
import useForceRender from "../../../../hooks/useForceRender";
import Tooltip from "../../../../components/Tooltip";


interface IUserAddEditDialogProps extends IAddEditDialogProps<IUser> {
    enterprises: IEnterprise[];
}


const RegistrationStatusInactived = RegistrationStatusData[RegistrationStatusEnum.Inactived];

const AddEditDialog: React.FunctionComponent<IUserAddEditDialogProps> = ({
    open,
    handleClose,
    data,
    handleListRefresh = () => {},
    enterprises
}) => {
    const forceRender = useForceRender();

    const { translate } = useLanguageContext();

    const [fullName, setFullName] = useState<string>('');
    const [login, setLogin] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [password, setPassword] = useState<string>('');
    const [partnerId, setPartnerId] = useState<string>('');
    const [actived, setActived] = useState<boolean>(true);
    const [userAccessProfileEnterprise, setUserAccessProfileEnterprise] = useState<Partial<IUserAccessProfileEnterprise>[]>([]);
    const [allowAllEnterprisesViewedInPowerBi, setAllowAllEnterprisesViewedInPowerBi] = useState<boolean>(false);

    const [accessProfiles, setAccessProfiles] = useState<IAccessProfile[]>([]);


    useEffect(() => {
        if (open) {
            setPassword('');

            if (data) {
                setFullName(data.fullName);
                setLogin(data.login);
                setEmail(data.email);
                setPartnerId(data.partnerId);
                setActived(Number(data.registrationStatusId) === RegistrationStatusEnum.Actived);
                setUserAccessProfileEnterprise(data.enterprises);
                setAllowAllEnterprisesViewedInPowerBi(data.allowAllEnterprisesViewedInPowerBi);
            } else {
                setFullName('');
                setLogin('');
                setEmail('');
                setPartnerId('');
                setActived(true);
                setUserAccessProfileEnterprise([]);
                setAllowAllEnterprisesViewedInPowerBi(false);
            }
        }

        if (accessProfiles.length === 0) {
            const formData = FormHelper.Create(undefined);
            AccessProfileService.List(formData)
                .then(resp => {
                    const accessProfiles = (resp.data.accessProfiles as IAccessProfile[]).filter(ap => Number(ap.registrationStatusId) === RegistrationStatusEnum.Actived);
                    setAccessProfiles(accessProfiles);
                });
        }

        // eslint-disable-next-line
    }, [open]);


    const handleSubmit = useCallback((event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault();

        let formData = FormHelper.Create(event);
        if (data?.id) {
            formData = FormHelper.Add(formData, 'id', data.id);
        }

        UserService.AddEdit(formData).then(() => {
            handleClose();
            handleListRefresh();
        });
        
        // eslint-disable-next-line
    }, [data]);


    const handleChangeEnterpriseAccessProfile = useCallback((enterprise: IEnterprise, accessProfileId: number, userPartnerAccessToken: string) => {
        const userAccessProfileEnterpriseAux = userAccessProfileEnterprise;

        const index = userAccessProfileEnterpriseAux.findIndex(e => e.id === enterprise.id);
        if (index > -1) {
            userAccessProfileEnterpriseAux.splice(index, 1);
        }

        userAccessProfileEnterpriseAux.push({
            id: enterprise.id,
            accessProfileId: accessProfileId,
            userPartnerAccessToken: userPartnerAccessToken
        });
        setUserAccessProfileEnterprise(userAccessProfileEnterpriseAux);
        forceRender();

        // eslint-disable-next-line
    }, [enterprises, userAccessProfileEnterprise]);


    const dialogActions = useMemo(() => {
        return [
            <ActionButton action='cancel' onClick={handleClose} />,
            <ActionButton action='save' type='submit' />
        ];
        // eslint-disable-next-line
    }, [translate]);
    

    return (
        <Dialog
            open={open}
            handleClose={handleClose}
            title={translate(data ? 'edit' : 'add')}
            maxWidth='md'
            fullWidth={true}
            fullHeight={true}
            dialogActions={dialogActions}
            onSubmit={handleSubmit}
            className='dialogactions-spacebetween'
        >
            <Grid container columnSpacing={DefaultConstants.gridColumnSpacing}>
                <Grid item md={8} xs={12}>
                    <TextField 
                        label={translate('fullName')} 
                        name='fullName' 
                        value={fullName} 
                        onChange={e => ValidationHelper.MaxLength(e.target.value, 100, setFullName)}
                    />
                </Grid>

                <Grid item md={4} xs={12}>
                    <TextField 
                        label={translate('partnerId')} 
                        name='partnerId' 
                        value={partnerId} 
                        required={!data?.administrator}
                        onChange={e => ValidationHelper.MaxLength(e.target.value, 20, setPartnerId)}
                    />
                </Grid>

                <Grid item md={6} xs={12}>
                    <TextField 
                        label={translate('user')} 
                        name='login' 
                        value={login} 
                        onChange={e => ValidationHelper.MaxLength(e.target.value.toUpperCase(), 20, setLogin)}
                    />
                </Grid>

                <Grid item md={6} xs={12}>
                    <TextField 
                        type='password'
                        label={translate('password')} 
                        name='password' 
                        value={password} 
                        required={!data?.id}
                        onChange={e => ValidationHelper.MaxLength(e.target.value.trim(), 20, setPassword)}
                    />
                </Grid>

                <Grid item md={6} xs={12}>
                    <TextField 
                        type='email'
                        label={translate('email')} 
                        name='email' 
                        value={email} 
                        onChange={e => ValidationHelper.MaxLength(e.target.value, 100, setEmail)}
                    />
                </Grid>

                <Grid item md={6} xs={12}>
                    <TextField 
                        label={translate('token')}
                        value={data?.token ?? ''} 
                        disabled={true}
                        required={false}
                    />
                </Grid>

                <Grid item xs={12} className='textalign-right'>
                    <Switch
                        label={translate('actived')}
                        name='registrationStatusId'
                        value={actived ? RegistrationStatusEnum.Actived : RegistrationStatusEnum.Inactived}
                        checked={actived}
                        onChange={e => setActived(e.target.checked)}
                    />
                </Grid>

                <Grid item xs={12} marginY={4}>
                    <Divider />
                </Grid>

                <Grid item xs={12} marginBottom={2}>
                    <DialogSectionTitle title={translate('enterprises')} />
                </Grid>

                <Grid item xs={12}>
                    <Grid container rowSpacing={DefaultConstants.gridRowSpacing} columnSpacing={DefaultConstants.gridColumnSpacing}>
                        {
                            enterprises.map(enterprise => {
                                const accessProfileId = userAccessProfileEnterprise.find(e => e.id === enterprise.id)?.accessProfileId ?? 0;
                                const userPartnerAccessToken = userAccessProfileEnterprise.find(e => e.id === enterprise.id)?.userPartnerAccessToken ?? '';

                                return <Grid item md={4} sm={6} xs={12} key={enterprise.id} alignItems='stretch'>
                                    <Grid item className='enterprisebox-grid'>
                                        <Grid item xs={12} className='enterprisebox-header'>
                                            <Typography className='overflow-hidden textoverflow-ellipsis whitespace-nowrap'>
                                                {enterprise.name}
                                            </Typography>

                                            {
                                                Number(enterprise.registrationStatusId) === RegistrationStatusEnum.Inactived && (
                                                    <Tooltip title={translate('inactivedEnterprise')}>
                                                        <IconButton size="small">
                                                            <RegistrationStatusInactived.Icon className='fontsize-14' />
                                                        </IconButton>
                                                    </Tooltip>
                                                )
                                            }
                                        </Grid>

                                        <Grid item xs={12} marginY={1} marginBottom={2}>
                                            <Divider />
                                        </Grid>

                                        <Grid item xs={12}>
                                            <SelectField 
                                                label={translate('accessProfile')}
                                                name={`userAccessProfileEnterprises[${enterprise.id}][accessProfileId]`} 
                                                value={accessProfileId}
                                                onChange={e => handleChangeEnterpriseAccessProfile(enterprise, e.target.value, userPartnerAccessToken)}
                                                margin='none'
                                                data={[
                                                    { value: 0, text: translate('blockedAccess') },
                                                    ...accessProfiles.map(a => ({ value: a.id, text: a.description }))
                                                ]}
                                            />
                                        </Grid>

                                        <Grid item xs={12} paddingY={1}>
                                            <TextField 
                                                label={translate('accessToken')}
                                                name={`userAccessProfileEnterprises[${enterprise.id}][userPartnerAccessToken]`} 
                                                value={userPartnerAccessToken} 
                                                margin="none"
                                                onChange={e => handleChangeEnterpriseAccessProfile(enterprise, accessProfileId, e.target.value)}
                                                required={false}
                                                disabled={accessProfileId === 0}
                                            />
                                        </Grid>
                                    </Grid>
                                </Grid>;
                            })
                        }
                    </Grid>
                </Grid>

                <Grid item xs={12} marginY={4}>
                    <Divider />
                </Grid>

                <Grid item xs={12} marginBottom={2}>
                    <DialogSectionTitle title={translate('parameters')} />
                </Grid>

                <Grid item xs={12} marginTop={2} marginLeft={1}>
                    <Switch
                        label={translate('allowAllEnterprisesViewedInPowerBi')}
                        name='allowAllEnterprisesViewedInPowerBi'
                        value={allowAllEnterprisesViewedInPowerBi}
                        checked={allowAllEnterprisesViewedInPowerBi}
                        onChange={e => setAllowAllEnterprisesViewedInPowerBi(e.target.checked)}
                    />
                </Grid>
            </Grid>
        </Dialog>
    );
}

export default AddEditDialog;